/**
 * Service layer — Checklist domain
 *
 * Owns: Checklist Items (tblU6m44TpQ9zkpso),
 *       Checklist Templates (tbl6SAgPLXMHm5Uja)
 */

import { apiGet, apiGetAll, apiGetRecord, apiPatch, apiPostBatch, apiDelete } from './_api.js'
import { refreshMilestoneMirror } from './schedule.js'

// Helper used inside this module only
function _getFirst(val) { return Array.isArray(val) ? val[0] : val }

const PHASE_PREFIXES = ['DD', 'DAPC', 'PPC / BID', 'EXT', 'INT', 'AB']

/**
 * Normalise a phase prefix to the field-name prefix used in Airtable.
 * The phase display value 'PPC / BID' becomes 'PPC BID' in field names
 * (e.g. 'PPC BID Complete', 'PPC BID Not Required').
 * All other prefixes are unchanged.
 * @param {string} prefix — raw phase prefix ('DD', 'DAPC', 'PPC / BID', 'EXT', 'INT')
 * @returns {string} Airtable field-name prefix
 */
export function fieldPfx(prefix) {
  return prefix === 'PPC / BID' ? 'PPC BID' : prefix
}

const TBL_CL  = 'tblU6m44TpQ9zkpso'
const TBL_CLT = 'tbl6SAgPLXMHm5Uja'

/**
 * Fetch Checklist Item Templates linked to a milestone template,
 * sorted by Sort Order.
 * @param {string} milestoneTemplateId — Project Deliverable Type record ID
 * @returns {Promise<object[]>} Raw Airtable records
 */
export function getChecklistTemplates(milestoneTemplateId) {
  return apiGet(
    TBL_CLT,
    'filterByFormula=' + encodeURIComponent('FIND("' + milestoneTemplateId + '",ARRAYJOIN({Record ID (from Project Deliverable Types)},","))')
    + '&sort%5B0%5D%5Bfield%5D=Sort+Order&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=100'
  )
}

/**
 * Give a newly created milestone its checklist. The server seeds it from the v2 templates
 * (Supabase) for the milestone's type; nothing new is written to Airtable (api/airtable.js's
 * checklist seam, since 2026-09-23). ONE request on purpose: the server seeds the whole checklist
 * on the first request it sees, so splitting this into batches of 10 (the old per-template shape)
 * made the second batch fail with "already has its checklist" on any type with more than ten
 * Airtable templates, and the wizard reported "Creation failed" (Brandon, 2026-10-12). It is also
 * no longer gated on the Airtable template count, which skipped types that only have v2 templates
 * (RFI Response, Submittal Review) and left them with no checklist until the daily backstop ran.
 * A type with no v2 templates simply gets no checklist.
 * @param {string} milestoneId
 */
export function seedMilestoneChecklist(milestoneId) {
  return apiPostBatch(TBL_CL, [{ 'Deliverable': [milestoneId] }])
}

/**
 * Fetch Checklist Items for a milestone, sorted by category then item sort order.
 * Used by MilestoneSections (inline mode) to lazy-load checklist data after page refresh.
 * @param {string} msId — Milestone (Project Deliverable) record ID
 * @returns {Promise<object[]>} Raw Airtable records
 */
export function getMilestoneChecklistItems(msId) {
  return apiGet(
    TBL_CL,
    'filterByFormula=' + encodeURIComponent('FIND("' + msId + '",ARRAYJOIN({Record ID (from Deliverable)},","))')
    + '&sort%5B0%5D%5Bfield%5D=Category+Sort&sort%5B0%5D%5Bdirection%5D=asc'
    + '&sort%5B1%5D%5Bfield%5D=Sort+Order+(from+Deliverable+Checklist+Item+Template)&sort%5B1%5D%5Bdirection%5D=asc'
    + '&maxRecords=100'
  )
}

/**
 * Complete or uncomplete a milestone Checklist Item — the same Airtable write
 * window.ajScCompleteItem (outline.js) performs, exposed for tools outside the outline engine
 * (e.g. the Field Observation capture UI) that don't share its clCache. Attribution ('Completed
 * by') is only set/cleared here; 'Completed Date' is an Airtable lastModifiedTime field the
 * server stamps on its own.
 * @param {string} itemId — Checklist Item record ID
 * @param {boolean} complete
 * @param {string|null} [memberId] — current user's Member record ID, for 'Completed by'
 */
export function completeChecklistItem(itemId, complete, memberId) {
  const fields = { 'Complete': complete }
  fields['Completed by'] = (complete && memberId) ? [memberId] : []
  return apiPatch(TBL_CL, itemId, fields).then(rec => {
    // Cutover slice one: a ticked item changes the parent milestone's Full-Kit counts, which the
    // Flight Board's full-kit badge reads off the mirror — refresh that one milestone now.
    const parent = rec && rec.fields && rec.fields['Deliverable']
    const msId = Array.isArray(parent) ? parent[0] : parent
    if (msId) refreshMilestoneMirror([msId])
    return rec
  })
}

/**
 * How many of a drawing sheet's checklist items for one drawing release are still open (neither
 * Complete nor Not Required). The one rule behind "no drawing goes onto a milestone until its sheet
 * checklist is done" — the sheet-row PDF upload (DrawingSheetList) and AlderCAD's Send to a
 * milestone both call it. Returns 0 when there is no release to check against (revision
 * milestones, ASIs: resolveMilestonePhasePrefix gives null by design). Throws if the checklist
 * can't be read; callers let the drawing through then, as the row upload always has.
 * @param {string} sheetId — Drawing Sheet record ID
 * @param {string|null} phasePrefix — the milestone's release ('DD', 'DAPC', 'PPC / BID', ...)
 * @param {Array} [items] — the sheet's checklist items when already loaded
 */
export async function openSheetChecklistCount(sheetId, phasePrefix, items) {
  if (!sheetId || !phasePrefix) return 0
  const list = Array.isArray(items) ? items : await getSheetChecklistItems(sheetId)
  const fp = fieldPfx(phasePrefix)
  return (list || []).filter(i => {
    const dr = i.fields['Drawing Release (from Deliverable Checklist Item Template)']
    const inPhase = Array.isArray(dr) ? dr.includes(phasePrefix) : dr === phasePrefix
    return inPhase && !(i.fields[fp + ' Complete'] || i.fields[fp + ' Not Required'])
  }).length
}

/**
 * Fetch Checklist Items linked to a Drawing Sheet (sheet-level checklists).
 * Returns all fields — do not filter with fields[] as linked/lookup fields return empty.
 * Items are filtered client-side by 'Drawing Release (from Deliverable Checklist Item Template)'.
 * @param {string} sheetId — Drawing Sheet record ID
 */
export function getSheetChecklistItems(sheetId) {
  return apiGet(
    TBL_CL,
    'filterByFormula=' + encodeURIComponent('FIND("' + sheetId + '",ARRAYJOIN({Record ID (from Drawing Sheet)},","))')
    + '&sort%5B0%5D%5Bfield%5D=Sort+Order+(from+Deliverable+Checklist+Item+Template)&sort%5B0%5D%5Bdirection%5D=asc'
    + '&maxRecords=200'
  )
}

/**
 * Bulk-load checklist items for multiple Drawing Sheets.
 * Batches sheetIds by 20 to stay within Airtable formula length limits.
 * Returns a Map: sheetId → items[]
 *
 * Used for eager-loading progress counts on collapsed sheet rows.
 *
 * @param {string[]} sheetIds — Drawing Sheet record IDs
 * @returns {Promise<Map<string, object[]>>}
 */
export async function getProjectSheetChecklistItems(sheetIds) {
  if (!sheetIds || !sheetIds.length) return new Map()
  const results = new Map()
  const BATCH = 20
  for (let i = 0; i < sheetIds.length; i += BATCH) {
    const batch = sheetIds.slice(i, i + BATCH)
    const formula = batch.length === 1
      ? 'FIND("' + batch[0] + '",ARRAYJOIN({Record ID (from Drawing Sheet)},","))'
      : 'OR(' + batch.map(id => 'FIND("' + id + '",ARRAYJOIN({Record ID (from Drawing Sheet)},","))').join(',') + ')'
    const records = await apiGetAll(
      TBL_CL,
      'filterByFormula=' + encodeURIComponent(formula)
      + '&sort%5B0%5D%5Bfield%5D=Sort+Order+(from+Deliverable+Checklist+Item+Template)&sort%5B0%5D%5Bdirection%5D=asc'
    )
    for (const r of records) {
      const sid = (r.fields['Drawing Sheet'] || [])[0]
      if (!sid) continue
      if (!results.has(sid)) results.set(sid, [])
      results.get(sid).push(r)
    }
  }
  // Guarantee completeness: every requested id has an entry.
  // Any id absent from the Map genuinely has no checklist items.
  for (const id of sheetIds) {
    if (!results.has(id)) results.set(id, [])
  }
  return results
}

/**
 * Write a phase-specific action to a Drawing Sheet checklist item.
 * Completion and skip fields are per-phase (e.g. 'DD Complete'); delegation is global.
 *
 * @param {string} itemId — Checklist Item record ID
 * @param {string} phasePrefix — e.g. 'DD', 'DAPC', 'PPC / BID', 'EXT', 'INT'
 * @param {'complete'|'uncomplete'|'notRequired'|'markRequired'|'delegate'|'removeDelegate'} action
 * @param {string|null} memberId — required for complete, notRequired, delegate actions
 * @param {string|null} [reason] — optional reason text for 'notRequired' action
 */
export function updateSheetChecklistItem(itemId, phasePrefix, action, memberId, reason) {
  const fp = fieldPfx(phasePrefix)
  let fields = {}
  switch (action) {
    case 'complete':
      fields[fp + ' Complete'] = true
      if (memberId) fields[fp + ' Completed by'] = [memberId]
      break
    case 'uncomplete':
      fields[fp + ' Complete'] = false
      fields[fp + ' Completed by'] = []
      break
    case 'notRequired':
      fields[fp + ' Not Required'] = true
      if (memberId) fields[fp + ' Not Required Member'] = [memberId]
      if (reason) fields[fp + ' Not Required Reason'] = reason
      break
    case 'markRequired':
      fields[fp + ' Not Required'] = false
      fields[fp + ' Not Required Member'] = []
      fields[fp + ' Not Required Reason'] = ''
      break
    // ('delegate' / 'removeDelegate' left with checklist delegation, 2026-09-02 —
    //  docs/FLOW_RECONCILIATION_PLAN.md decision 6. `Support Assignment` is frozen history.)
  }
  return apiPatch(TBL_CL, itemId, fields)
}

/**
 * Reset drawing sheet checklist — syncs checklist item instances to templates.
 *
 * Fetches all Checklist Item Templates whose Type is "Drawing" (sheet-level templates),
 * creates missing instances for this sheet, and deletes orphan instances (no matching
 * template) only if not yet completed or skipped for any phase.
 *
 * @param {string} sheetId — Drawing Sheet record ID
 */
export async function resetSheetChecklist(sheetId) {
  // 1. Fetch the Drawing Sheet to get its linked template — use direct record GET,
  //    not a RECORD_ID() filter (that formula field returns empty via the Airtable API).
  const TBL_SHEET = 'tblXFwxWKECjoVj9O'
  const sheetRecord = await apiGetRecord(TBL_SHEET, sheetId)
  const sheetTemplateId = (sheetRecord?.fields?.['Drawing Sheet Template'] || [])[0]
  if (!sheetTemplateId) return

  // 2. Fetch only templates linked to THIS Drawing Sheet Template
  const templates = await apiGet(
    TBL_CLT,
    'filterByFormula=' + encodeURIComponent(
      'FIND("' + sheetTemplateId + '",ARRAYJOIN({Record ID (from Drawing Sheet Template)},","))'
    )
    + '&sort%5B0%5D%5Bfield%5D=Sort+Order&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=200'
  )

  // 2. Fetch existing items for this sheet
  const existing = await getSheetChecklistItems(sheetId)

  // 3. Map template ID → first matching item (dedup: prefer done > skipped > incomplete)
  const byTpl = {}
  existing.forEach(item => {
    const tplId = _getFirst(item.fields['Deliverable Checklist Item Template'])
    if (!tplId) return
    const prev = byTpl[tplId]
    if (!prev) { byTpl[tplId] = item; return }
    // Score: done (2) > skipped (1) > incomplete (0). Use fieldPfx() for correct Airtable field names.
    const score = i => PHASE_PREFIXES.reduce((s, p) => s + (i.fields[fieldPfx(p) + ' Complete'] ? 2 : i.fields[fieldPfx(p) + ' Not Required'] ? 1 : 0), 0)
    if (score(item) > score(prev)) byTpl[tplId] = item
  })

  // 4. Create missing items (templates with no existing instance)
  const toCreate = templates
    .filter(t => !byTpl[t.id])
    .map(t => ({
      'Drawing Sheet':                       [sheetId],
      'Deliverable Checklist Item Template': [t.id],
    }))

  // 5. Delete orphan items (existing items whose template is no longer in the set)
  //    Only delete if the item has no completions or skips recorded for any phase
  const templateIds = new Set(templates.map(t => t.id))
  const toDelete = existing.filter(item => {
    const tplId = _getFirst(item.fields['Deliverable Checklist Item Template'])
    if (templateIds.has(tplId)) return false  // still valid
    const hasDoneOrSkip = PHASE_PREFIXES.some(p =>
      item.fields[fieldPfx(p) + ' Complete'] || item.fields[fieldPfx(p) + ' Not Required']
    )
    return !hasDoneOrSkip  // only delete if untouched
  })

  // No checklist at all and nothing to copy from Airtable: the template's items may live only in the
  // new system (written in Templates → Drawing Sheet). Send a bare seed probe — /api/airtable seeds
  // the sheet from those items, or creates nothing when there are none.
  if (!existing.length && !toCreate.length) {
    await apiPostBatch(TBL_CL, [{ 'Drawing Sheet': [sheetId] }])
    return
  }

  // Execute in parallel — batch creates, individual deletes
  await Promise.all([
    toCreate.length > 0 ? apiPostBatch(TBL_CL, toCreate) : Promise.resolve(),
    ...toDelete.map(item => apiDelete(TBL_CL, item.id)),
  ])
}

/** Mark a milestone Checklist Item not required, with its reason (the outline's own fields). */
export function markChecklistItemNotRequired(itemId, reason, memberId) {
  const fields = { 'Milestone Not Required': true, 'Milestone Not Required Reason': reason || '', 'Milestone Not Required Member': memberId ? [memberId] : [] }
  return apiPatch(TBL_CL, itemId, fields).then(rec => {
    const parent = rec && rec.fields && rec.fields['Deliverable']
    const msId = Array.isArray(parent) ? parent[0] : parent
    if (msId) refreshMilestoneMirror([msId])
    return rec
  })
}
